<template>
  <div class="calendar-filter" :style="filterIconStyle">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="modes">
        <action-button
          text="Hoy"
          :variant="
            `px-3 mr-2 font-weight-bold ` + (isToday ? 'orange' : 'light')
          "
          @click="setMode('today')"
        />
        <action-button
          text="Día"
          :variant="
            `px-3 mr-2 font-weight-bold ` + (isDay ? 'orange' : 'light')
          "
          @click="setMode('day')"
        />
        <action-button
          text="Semana"
          :variant="
            `px-3 mr-2 font-weight-bold ` + (isWeek ? 'orange' : 'light')
          "
          @click="setMode('week')"
        />
        <action-button
          text="Mes"
          :variant="`px-3 font-weight-bold ` + (isMonth ? 'orange' : 'light')"
          @click="setMode('month')"
        />
      </div>

      <action-button
        variant="success calendar-filter__toggle m-2"
        i="filter-button__icon"
        @click="togglePanel"
      >
        <span @click="togglePanel">Filtrar</span>
        <i class="fas fa-chevron-down ml-2" @click="togglePanel" />
      </action-button>
    </div>

    <div v-if="showPanel" class="calendar-filter__panel">
      <div class="calendar-filter__title">
        <span class="calendar-filter__title-icon">
          <span class="filter-button__icon" />
        </span>
        <span class="font-weight-bold">Filtrar Búsqueda</span>
      </div>

      <div class="calendar-filter__body">
        <div class="calendar-filter__grid">
          <custom-drop-down
            v-model="draft.academicYear"
            :options="academicYears"
            label="Año Fiscal:"
            fieldkey="id"
            fieldname="description"
            placeholder="Seleccione"
          />

          <custom-input
            v-model="draft.searchText"
            label="Estudiante, Número de SIE o  Número de Cita:"
            placeholder="Escriba"
            icon="fas fa-search text-orange"
            left-icon
            class="calendar-filter__input"
          />

          <custom-drop-down
            v-model="draft.status"
            :options="statuses"
            label="Estatus:"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
          />

          <custom-drop-down
            v-model="draft.meetingType"
            :options="meetingTypes"
            label="Tipo de Reunión:"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
          />
        </div>

        <div class="calendar-filter__actions">
          <button
            type="button"
            class="calendar-filter__apply-button"
            @click="applyFilters"
          >
            <span class="filter-button__icon" />
            <span>Filtrar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import academicYearService from '@/services/academicyear';
import compuAppointmentService from '@/modules/compu-appointment/api';
import compuAppointmentStatus from '@/utils/constants/compuAppointmentStatus';

const MIN_ACADEMIC_YEAR_ID = 33;
const AVAILABLE_STATUS_IDS = [
  compuAppointmentStatus.PendienteDeFirma,
  compuAppointmentStatus.Coordinada,
  compuAppointmentStatus.Confirmada,
  compuAppointmentStatus.Rechazada,
  compuAppointmentStatus.Recoordinada,
  compuAppointmentStatus.Celebrada,
  compuAppointmentStatus.NoCelebrada,
  compuAppointmentStatus.NoRespuesta,
  compuAppointmentStatus.Cancelada,
];

const NO_RESPONSE_STATUS = {
  id: compuAppointmentStatus.NoRespuesta,
  name: 'No respuesta',
  disabled: false,
};

export default {
  name: 'CompuCalendarFilters',
  components: {
    ActionButton,
    CustomInput,
    CustomDropDown,
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    meetingTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showPanel: false,
      academicYears: [],
      statuses: [],
      draft: this.getEmptyDraft(),
      resourcesLoaded: false,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    isToday() {
      return this.model.mode === 'today';
    },
    isDay() {
      return this.model.mode === 'day';
    },
    isWeek() {
      return this.model.mode === 'week';
    },
    isMonth() {
      return this.model.mode === 'month';
    },
    filterIconStyle() {
      const baseUrl = process.env.BASE_URL || '/';

      return {
        '--filter-icon-url': `url("${baseUrl}img/icons/bars-filter.svg")`,
      };
    },
  },
  methods: {
    getEmptyDraft() {
      return {
        academicYear: null,
        searchText: '',
        status: null,
        meetingType: null,
      };
    },
    cloneAppliedFilters() {
      return {
        academicYear: this.model.academicYear || null,
        searchText: this.model.searchText || '',
        status: this.model.status || null,
        meetingType: this.model.meetingType || null,
      };
    },
    async togglePanel() {
      if (this.showPanel) {
        this.showPanel = false;
        return;
      }

      this.draft = this.cloneAppliedFilters();
      this.showPanel = true;
      await this.loadResources();
    },
    async loadResources() {
      if (this.resourcesLoaded) {
        return;
      }

      const [academicYearsResponse, statuses] = await Promise.all([
        academicYearService.getAcademicYearFromMin({
          minYearId: MIN_ACADEMIC_YEAR_ID,
          yearType: 'FUTURE',
        }),
        compuAppointmentService.fetchStatuses(),
      ]);

      this.academicYears = academicYearsResponse?.data || [];
      this.statuses = (statuses || []).filter((status) =>
        AVAILABLE_STATUS_IDS.includes(status.id)
      );

      if (
        !this.statuses.some((status) => status.id === NO_RESPONSE_STATUS.id)
      ) {
        this.statuses.push(NO_RESPONSE_STATUS);
      }

      this.resourcesLoaded = true;
    },
    setMode(mode) {
      this.model.mode = mode;
    },
    applyFilters() {
      this.model = {
        ...this.model,
        academicYear: this.draft.academicYear,
        searchText: (this.draft.searchText || '').trim(),
        status: this.draft.status,
        meetingType: this.draft.meetingType,
      };
      this.showPanel = false;
      this.$emit('apply');
    },
  },
};
</script>

<style scoped>
.calendar-filter {
  position: relative;
}

.calendar-filter__toggle {
  align-items: center;
  display: inline-flex;
  font-weight: 600;
  gap: 6px;
  min-height: 50px;
  padding: 0 18px;
}

.calendar-filter__toggle ::v-deep .filter-button__icon,
.filter-button__icon {
  background-image: var(--filter-icon-url);
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  display: inline-block;
  height: 13px;
  width: 15px;
}

.calendar-filter__panel {
  background: white;
  box-shadow: 0px 10px 30px 0px #00000012;
  max-width: calc(100vw - 96px);
  overflow: visible;
  position: absolute;
  right: 0;
  top: 62px;
  width: 780px;
  z-index: 20;
}

.calendar-filter__title {
  align-items: center;
  display: flex;
  gap: 10px;
  min-height: 75px;
  padding: 0 28px;
}

.calendar-filter__title-icon {
  align-items: center;
  background: #ff7058;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(17, 41, 27, 0.18);
  color: white;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.calendar-filter__title-icon .filter-button__icon {
  height: 12px;
  width: 14px;
}

.calendar-filter__grid {
  display: grid;
  gap: 18px 20px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.calendar-filter__body {
  background: linear-gradient(180deg, #f4f6f3 0%, #ffffff 72%);
  padding: 24px 28px 28px;
}

.calendar-filter__body ::v-deep .custom-ddl div .v-select .vs__dropdown-toggle,
.calendar-filter__body ::v-deep .custom-ddl .v-select.custom-v-select,
.calendar-filter__body ::v-deep .custom-ddl .vs__selected-options,
.calendar-filter__body ::v-deep .custom-ddl .vs__search,
.calendar-filter__body ::v-deep .custom-ddl .vs__actions {
  background-color: #fff !important;
}

.calendar-filter__body ::v-deep .custom-ddl div .v-select .vs__dropdown-toggle {
  border: 1px solid #dedede;
  border-radius: 4px;
  box-sizing: border-box;
  height: 38px;
  overflow: visible;
}

.calendar-filter__body ::v-deep .custom-ddl .vs__selected,
.calendar-filter__body ::v-deep .custom-ddl .vs__search {
  margin: 0;
}

.calendar-filter__input ::v-deep label {
  color: #6c757d;
  font-size: 14px;
  font-weight: 400;
  height: 19px;
  line-height: 19px;
  margin-bottom: 6px;
}

.calendar-filter__input ::v-deep .form-control {
  background-color: #fff;
  border-color: #dedede;
  height: 38px;
}

.calendar-filter__input ::v-deep .form-control-icon-normal.left {
  color: #ff7058;
}

.calendar-filter__actions {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}

.calendar-filter__apply-button {
  align-items: center;
  background-color: #2db782;
  border: 0;
  border-radius: 4px;
  color: white;
  display: inline-flex;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 44px;
  min-width: 380px;
}

.modes ::v-deep .orange {
  min-width: 99px;
  background-color: #ff7058 !important;
  color: white !important;
  box-shadow: none !important;
}

.modes ::v-deep .light {
  min-width: 99px;
  background-color: #f4f6f3 !important;
  color: #36484b !important;
  box-shadow: none !important;
}
</style>
